import { MessageDirection } from '@chatscope/use-chat';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { v4 as uuidv4 } from 'uuid';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import * as Sentry from '@sentry/react';

export const ChatMode = {
  CLIENT: 'CLIENT',
  DOCUMENT: 'DOCUMENT',
} as const;

export type ChatMode = (typeof ChatMode)[keyof typeof ChatMode];

export const ChatState = {
  THINKING: 'THINKING',
  ANSWERING: 'ANSWERING',
  EDITING: 'EDITING',
  FINAL: 'FINAL',
} as const;

export type ChatState = (typeof ChatState)[keyof typeof ChatState];

export type FinalChunkData = {
  conversationId: string;
  editedContent: string;
  editedContentWithMarkers?: string | null;
  assistantMessage: string;
  versionId: string | null;
  versionNumber: number | null;
  versioningFailed: boolean;
};

type MessageState = 'SENT' | 'ANSWERING' | 'THINKING' | string;
type MessageContent = { state: MessageState; content: string };
export type Message = {
  id: string;
  response: MessageContent[];
  sender: string;
  direction: MessageDirection;
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
};

type StateChangeCallback = (state: ChatState, data?: FinalChunkData) => void;

type ClientChatOptions = {
  conversationId?: string;
  mode?: typeof ChatMode.CLIENT;
  onStateChange?: StateChangeCallback;
};

type DocumentChatOptions = {
  conversationId?: string;
  mode: typeof ChatMode.DOCUMENT;
  entityId: string;
  requestBodyBuilder: (message: string) => object;
  onStateChange?: StateChangeCallback;
};

type UseChatMessagesOptions = ClientChatOptions | DocumentChatOptions;

type ModeConfig = {
  handledStates: ChatState[];
  fireTransitionCallbacks: boolean;
};

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  [ChatMode.CLIENT]: {
    handledStates: [ChatState.THINKING, ChatState.ANSWERING],
    fireTransitionCallbacks: false,
  },
  [ChatMode.DOCUMENT]: {
    handledStates: [ChatState.THINKING, ChatState.ANSWERING, ChatState.EDITING, ChatState.FINAL],
    fireTransitionCallbacks: true,
  },
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const createUserMessage = (content: string): Message => {
  const response = [{ state: 'SENT', content }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    id: uuidv4(),
    response,
    sender: 'Me',
    direction: MessageDirection.Outgoing,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const createResponseMessage = (content?: string, id?: string): Message => {
  const response = content ? [{ state: 'ANSWERING', content }] : [];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    id: id || uuidv4(),
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

type ParsedChunk = {
  state: MessageState;
  content: string;
  data?: FinalChunkData;
};

const parseStreamChunk = (rawData: string): ParsedChunk | null => {
  try {
    const parsed = JSON.parse(rawData);
    return {
      state: parsed.state,
      content: parsed.content,
      data: parsed.data,
    };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const processContentTransforms = (content: string): string => {
  const transforms = [
    {
      regex: /<span\s+[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi,
      replacement: '$1',
    },

    // Answer content is sometimes incorrectly wrapped in a <response> tag
    { regex: /<response>(.*?)<\/response>/gis, replacement: '$1' },
  ];

  return transforms.reduce(
    (transformed, { regex, replacement }) => transformed.replace(regex, replacement),
    content,
  );
};

const computeMessageProperties = (
  response: MessageContent[],
): {
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
} => {
  const thinkingContent = processContentTransforms(
    response
      .filter(r => r.state !== 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const answerContent = processContentTransforms(
    response
      .filter(r => r.state === 'ANSWERING')
      .map(r => r.content)
      .join('\n\n'),
  );

  const currentState = response[response.length - 1]?.state || 'SENT';

  return { thinkingContent, answerContent, currentState };
};

const appendToResponse = (message: Message, state: MessageState, content: string): Message => {
  const transformedContent = processContentTransforms(content);
  const newResponse = [...message.response];

  const lastResponse = newResponse[newResponse.length - 1];
  if (lastResponse && lastResponse.state === state) {
    newResponse[newResponse.length - 1] = {
      ...lastResponse,
      content: lastResponse.content + transformedContent,
    };
  } else {
    newResponse.push({ state, content: transformedContent });
  }

  const { thinkingContent, answerContent, currentState } = computeMessageProperties(newResponse);

  return {
    ...message,
    response: newResponse,
    thinkingContent,
    answerContent,
    currentState,
  };
};

export function useChatMessages(options?: UseChatMessagesOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [fallbackConversationId] = useState(() => uuidv4());
  const conversationId = options?.conversationId ?? fallbackConversationId;
  const [latestIncomingId, setLatestIncomingId] = useState<string | null>(null);
  const [currentChatState, setCurrentChatState] = useState<ChatState | null>(null);
  const chatStateRef = useRef<ChatState | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const mode = options?.mode ?? ChatMode.CLIENT;
  const modeConfig = MODE_CONFIGS[mode];
  const onStateChangeRef = useRef(options?.onStateChange);
  onStateChangeRef.current = options?.onStateChange;

  const modeConfigRef = useRef(modeConfig);
  modeConfigRef.current = modeConfig;

  const updateChatState = useCallback((newState: ChatState | null) => {
    chatStateRef.current = newState;
    setCurrentChatState(newState);
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const addMessage = useCallback((message: Message): void => {
    setMessages(prev => [...prev, message]);
    if (message.direction === MessageDirection.Incoming) {
      setLatestIncomingId(message.id);
    }
  }, []);

  const handleIncomingStreamChunk = useCallback(
    (messageId: string, rawData: string): void => {
      const parsedChunk = parseStreamChunk(rawData);
      if (!parsedChunk) return;

      const { state, content, data } = parsedChunk;
      const config = modeConfigRef.current;

      if (state === ChatState.EDITING) {
        if (!config.handledStates.includes(ChatState.EDITING)) return;
        if (chatStateRef.current !== ChatState.EDITING) {
          updateChatState(ChatState.EDITING);
          onStateChangeRef.current?.(ChatState.EDITING);
        }
        return;
      }

      if (state === ChatState.FINAL) {
        if (!config.handledStates.includes(ChatState.FINAL)) return;
        updateChatState(ChatState.FINAL);
        onStateChangeRef.current?.(ChatState.FINAL, data);

        if (data?.assistantMessage) {
          setMessages(currentMessages => {
            const targetMessage = currentMessages.find(msg => msg.id === messageId);
            if (!targetMessage || targetMessage.answerContent.trim()) return currentMessages;
            const updatedMessage = appendToResponse(
              targetMessage,
              ChatState.ANSWERING,
              data.assistantMessage,
            );
            return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
          });
        }
        return;
      }

      if (state === ChatState.THINKING && chatStateRef.current !== ChatState.THINKING) {
        updateChatState(ChatState.THINKING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.THINKING);
        }
      } else if (state === ChatState.ANSWERING && chatStateRef.current !== ChatState.ANSWERING) {
        updateChatState(ChatState.ANSWERING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.ANSWERING);
        }
      }

      setMessages(currentMessages => {
        const targetMessage = currentMessages.find(msg => msg.id === messageId);
        if (!targetMessage) {
          const newMessage = createResponseMessage();
          newMessage.id = messageId;
          const updatedMessage = appendToResponse(newMessage, state, content);
          setLatestIncomingId(messageId);
          return [...currentMessages, updatedMessage];
        }

        const updatedMessage = appendToResponse(targetMessage, state, content);
        return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
      });
    },
    [updateChatState],
  );

  const handleStreamResponse = useCallback(
    async (response: Response): Promise<void> => {
      if (response.body === null) return;

      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .pipeThrough(new EventSourceParserStream())
        .getReader();

      let hasStreamStarted = false;
      const receivedMessageId = uuidv4();

      while (true) {
        const { value, done } = await reader.read();
        if (done || value.data === '\n\n') break;

        if (!hasStreamStarted && value !== undefined) {
          hasStreamStarted = true;
        }

        if (value.data) {
          handleIncomingStreamChunk(receivedMessageId, value.data);
        }
      }
    },
    [handleIncomingStreamChunk],
  );

  const fetchAndHandleStreamResponse = useCallback(
    async (url: string, body: object): Promise<void> => {
      updateChatState(null);

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch stream: ${response.status} ${errorText}`);
        }

        await handleStreamResponse(response);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        Sentry.captureException(error);
        addMessage(
          createResponseMessage(
            'An error occurred while processing your request, please wait a moment and try again. If the issue persists, please contact support.',
          ),
        );
        // Fire FINAL callback so consumers (e.g. edit page) can reset from EDITING state
        onStateChangeRef.current?.(ChatState.FINAL);
      } finally {
        updateChatState(null);
      }
    },
    [handleStreamResponse, updateChatState, addMessage],
  );

  const sendUserMessage = useCallback(
    async (content: string, clientId: string | null): Promise<void> => {
      Mixpanel.track(MixpanelEvents.SIESTA_MESSAGE_SENT, { message: content });

      addMessage(createUserMessage(content));

      if (mode === ChatMode.DOCUMENT) {
        const docOptions = options as DocumentChatOptions;
        const body = docOptions.requestBodyBuilder(content);
        await fetchAndHandleStreamResponse(
          `${API_URL}/documents/${docOptions.entityId}/multi-turn-edit`,
          body,
        );
        return;
      }

      if (!clientId) {
        addMessage(createResponseMessage('Please select a client to chat about'));
        return;
      }

      await fetchAndHandleStreamResponse(`${API_URL}/clients/${clientId}/chat`, {
        conversationId,
        message: content,
      });
    },
    [mode, options, fetchAndHandleStreamResponse, conversationId, addMessage],
  );

  const sendActionRequest = useCallback(
    async (action: 'agenda', clientId: string | null): Promise<void> => {
      if (!clientId) {
        addMessage(createResponseMessage('Please select a client to chat about'));
        return;
      }

      await fetchAndHandleStreamResponse(`${API_URL}/clients/${clientId}/siesta/${action}`, {
        conversationId,
        message: '',
      });
    },
    [fetchAndHandleStreamResponse, conversationId, addMessage],
  );

  const cancelStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    updateChatState(null);
  }, [updateChatState]);

  return {
    messages,
    sendUserMessage,
    sendActionRequest,
    latestIncomingId,
    clearMessages,
    conversationId,
    currentChatState,
    isEditing: currentChatState === ChatState.EDITING,
    cancelStreaming,
  };
}
